import { Moon, Club, Gem, Hand, Hash, Feather, ShieldCheck, Video, Stars, HeartHandshake } from "lucide-react";
import { IMAGES, waLink } from "@/lib/site";
import { Reveal } from "@/components/Reveal";
import { GoldDivider } from "@/components/GoldDivider";
import { useLang } from "@/i18n";

const ICONS = {
    tarot: Moon,
    cartas: Club,
    runas: Gem,
    mano: Hand,
    astrologia: Stars,
    consejeria: HeartHandshake,
    numerologia: Hash,
    limpias: Feather,
    proteccion: ShieldCheck,
    videollamadas: Video,
};

const SERVICE_IMAGES = {
    tarot: IMAGES.tarot,
    cartas: IMAGES.spanishCards,
    runas: IMAGES.runes,
    mano: IMAGES.palmistry,
    astrologia: IMAGES.hero,
    consejeria: "/images/shop/san-miguel.jpg",
    numerologia: IMAGES.apothecary,
    limpias: IMAGES.candles,
    proteccion: IMAGES.lantern,
    videollamadas: IMAGES.crystalBall,
};

export const Services = () => {
    const { t } = useLang();
    const items = t("services.items");

    return (
        <section id="servicios" data-testid="services-section" className="relative py-24 sm:py-32 px-5 sm:px-8 bg-ink">
            <div
                className="absolute inset-0 pointer-events-none candle-glow"
                style={{ background: "radial-gradient(ellipse 55% 40% at 85% 8%, rgba(74,14,28,0.35) 0%, transparent 65%)" }}
                aria-hidden="true"
            />
            <div className="relative max-w-7xl mx-auto">
                <Reveal className="text-center mb-16">
                    <p className="font-accent text-[11px] tracking-[0.4em] uppercase text-gold-soft mb-4">
                        {t("services.eyebrow")}
                    </p>
                    <h2 className="font-display font-semibold gold-text text-4xl sm:text-5xl tracking-wide">
                        {t("services.title")}
                    </h2>
                    <GoldDivider className="my-7" />
                    <p className="text-sm sm:text-base text-cream/65 font-light max-w-2xl mx-auto leading-relaxed">
                        {t("services.intro")}
                    </p>
                </Reveal>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {items.map((s, i) => {
                        const Icon = ICONS[s.id];
                        return (
                            <Reveal key={s.id} delay={Math.min(i * 0.07, 0.4)}>
                                <article
                                    data-testid={`service-card-${s.id}`}
                                    className="group h-full flex flex-col bg-surface border border-gold/15 rounded-sm p-7 hover:border-gold/40 hover:shadow-[0_0_26px_rgba(212,175,55,0.14)] hover:-translate-y-1 transition-[border-color,box-shadow,transform] duration-300"
                                >
                                    <div className="relative w-20 h-20 mb-6">
                                        <img
                                            src={SERVICE_IMAGES[s.id]}
                                            alt={s.title}
                                            loading="lazy"
                                            className="w-20 h-20 rounded-full object-cover border border-gold/40 shadow-[0_0_18px_rgba(212,175,55,0.18)] group-hover:shadow-[0_0_24px_rgba(212,175,55,0.35)] group-hover:border-gold/60 transition-[box-shadow,border-color] duration-300"
                                        />
                                        <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-ink border border-gold/45 flex items-center justify-center">
                                            <Icon className="w-3.5 h-3.5 text-gold" strokeWidth={1.6} />
                                        </span>
                                    </div>
                                    <h3 className="font-display text-xl font-semibold text-cream tracking-wide mb-3">
                                        {s.title}
                                    </h3>
                                    <p className="text-sm text-cream/60 font-light leading-relaxed flex-1">
                                        {s.desc}
                                    </p>
                                    <a
                                        href={waLink(t("wa.servicePrefix") + s.title + t("wa.serviceSuffix"))}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`service-book-${s.id}`}
                                        className="mt-6 inline-flex justify-center border border-gold/45 text-gold font-accent text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 rounded-sm hover:bg-gold/10 transition-colors duration-300"
                                    >
                                        {t("services.cta")}
                                    </a>
                                </article>
                            </Reveal>
                        );
                    })}
                </div>

                <Reveal className="mt-12">
                    <div
                        data-testid="limpias-walkin-banner"
                        className="border border-gold/35 bg-burgundy/20 rounded-sm px-6 py-6 text-center max-w-3xl mx-auto"
                    >
                        <p className="font-accent text-xs sm:text-sm tracking-[0.22em] uppercase text-cream">
                            {t("services.banner1")}
                        </p>
                        <p className="font-display italic text-lg text-gold mt-2">{t("services.banner2")}</p>
                        <p className="text-xs text-cream/55 font-light mt-3 tracking-wide">
                            {t("services.banner3")}
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
